import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { setAuthToken } from '../api/client';
import { projectsApi, Project } from '../api/projects';
import { flagsApi, Flag } from '../api/flags';

interface User {
  email: string;
  token: string;
}

interface AppContextValue {
  user: User | null;
  projects: Project[];
  currentProject: Project | null;
  flags: Flag[];
  loading: boolean;
  error: string | null;
  login: (email: string, token: string) => void;
  logout: () => void;
  selectProject: (projectId: string) => void;
  loadProjects: () => Promise<void>;
  loadFlags: (projectId: string) => Promise<void>;
  createProject: (name: string) => Promise<Project>;
  deleteProject: (projectId: string) => Promise<void>;
  createFlag: (projectId: string, data: Partial<Flag>) => Promise<Flag>;
  updateFlag: (flagId: string, data: Partial<Flag>) => Promise<Flag>;
  deleteFlag: (flagId: string) => Promise<void>;
}

const AppContext = createContext<AppContextValue | null>(null);

export function AppProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [currentProject, setCurrentProject] = useState<Project | null>(null);
  const [flags, setFlags] = useState<Flag[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const login = useCallback((email: string, token: string) => {
    const u = { email, token };
    setUser(u);
    setAuthToken(token);
    sessionStorage.setItem('haki_user', JSON.stringify(u));
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    setProjects([]);
    setCurrentProject(null);
    setFlags([]);
    setAuthToken(null);
    sessionStorage.removeItem('haki_user');
  }, []);

  // Restore session
  useEffect(() => {
    const stored = sessionStorage.getItem('haki_user');
    if (stored) {
      const u: User = JSON.parse(stored);
      setUser(u);
      setAuthToken(u.token);
    }
  }, []);

  const loadProjects = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await projectsApi.list();
      setProjects(data);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadFlags = useCallback(async (projectId: string) => {
    setLoading(true);
    setError(null);
    try {
      const data = await flagsApi.list(projectId);
      setFlags(data);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load flags');
    } finally {
      setLoading(false);
    }
  }, []);

  const selectProject = useCallback((projectId: string) => {
    const project = projects.find(p => p.projectId === projectId) || null;
    setCurrentProject(project);
  }, [projects]);

  const createProject = useCallback(async (name: string) => {
    const project = await projectsApi.create(name);
    setProjects(prev => [...prev, project]);
    return project;
  }, []);

  const deleteProject = useCallback(async (projectId: string) => {
    await projectsApi.delete(projectId);
    setProjects(prev => prev.filter(p => p.projectId !== projectId));
    if (currentProject?.projectId === projectId) {
      setCurrentProject(null);
      setFlags([]);
    }
  }, [currentProject]);

  const createFlag = useCallback(async (projectId: string, data: Partial<Flag>) => {
    const flag = await flagsApi.create(projectId, data);
    setFlags(prev => [...prev, flag]);
    return flag;
  }, []);

  const updateFlag = useCallback(async (flagId: string, data: Partial<Flag>) => {
    const flag = await flagsApi.update(flagId, data);
    setFlags(prev => prev.map(f => f.flagId === flagId ? flag : f));
    return flag;
  }, []);

  const deleteFlag = useCallback(async (flagId: string) => {
    await flagsApi.delete(flagId);
    setFlags(prev => prev.filter(f => f.flagId !== flagId));
  }, []);

  return (
    <AppContext.Provider value={{
      user, projects, currentProject, flags, loading, error,
      login, logout, selectProject, loadProjects, loadFlags,
      createProject, deleteProject, createFlag, updateFlag, deleteFlag,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
}
